import { useState } from 'react'
import Stripe from 'stripe'

import { httpService } from '../services/httpService'
import useToasterHelper from '../helpers/ToasterHelper'

import { MESSAGES_CONST } from '../const/messages-const'

import { useAppDispatch, useAppSelector } from '../store/hooks'
import { selectProfileData } from '../store/user/userSlice'
import useProfileHook from './users/competitor/profile/useProfileHook'

import {
  setConnectAccountDetail,
  setConnectAllPayouts,
  setConnectAvailableBalance,
  setPayoutsTotal,
} from '../store/finances/financesSlice'

const useFinances = () => {
  const dispatch = useAppDispatch()

  const toastFunctions = useToasterHelper()
  const { updateStripeConnectDetails } = useProfileHook()

  const profileData = useAppSelector(selectProfileData)

  const [transactionLoading, setTransactionLoading] = useState(false)

  const onCancelPayout = async (payoutId: string, stripeConnectAccountId: string) => {
    setTransactionLoading(true)

    try {
      await httpService({
        url: 'cancel_payout',
        method: 'POST',
        data: {
          payoutId: payoutId,
          stripeConnectAccountId: stripeConnectAccountId,
        },
      })
      toastFunctions.success({ message: 'Transfer canceled successfully' })
    } catch (err: any) {
      toastFunctions.error({
        message: err?.response?.data?.originalError || MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setTransactionLoading(false)
    }
  }

  const removeStripeAccountFromDb = async (showMessage?: boolean) => {
    const response = await updateStripeConnectDetails(null, null)
    if (response?.status) {
      if (showMessage)
        toastFunctions.success({
          message: MESSAGES_CONST.STRIPE_ACCOUNT_DELETE,
        })
    }
  }

  const getTransactions = async () => {
    let total = 0

    if (!!profileData?.userStripeAccountId) {
      try {
        setTransactionLoading(true)

        const {
          data,
        }: {
          data: {
            account: Stripe.Account
            payouts: Stripe.Payout[]
            balance: Stripe.Balance
          }
        } = await httpService({
          method: 'GET',
          url: `fetch_all_organizer_transactions/${profileData?.userStripeAccountId}`,
        })

        dispatch(setConnectAvailableBalance(data.balance.instant_available))
        dispatch(setConnectAccountDetail(data.account.external_accounts?.data))

        if (data?.payouts?.[0]) {
          const payouts = data.payouts.reduce((acc: any[], current) => {
            acc.push({
              ...current,
              accountNumber: data.account.external_accounts?.data[0]?.last4,
            })
            return acc
          }, [])

          data?.payouts.forEach((item) => {
            total += item.amount
          })

          dispatch(setConnectAllPayouts(payouts))
          dispatch(setPayoutsTotal((total / 100).toFixed(2)))
        }
      } catch (err: any) {
        let responseFromServer = err?.response?.data
        let message = responseFromServer?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG

        if (responseFromServer?.data?.removeAccount) await removeStripeAccountFromDb()

        toastFunctions.error({
          message,
        })
      } finally {
        setTransactionLoading(false)
      }
    }
  }

  return {
    onCancelPayout,
    getTransactions,
    transactionLoading,
  }
}

export default useFinances
